// Layer 1 (Data Schema): the time axis under every Reading — one seeded series
// per quantity, read through a session cursor (spec-vs-actual mapping §2.3;
// #197 specvactual.B5).
//
// A Reading is "now"; a ReadingSeries is the history "now" is the last point
// of. The store is seed JSON generated once by `scripts/seed/generate-series.mjs`
// into `src/versions/v1/shared/bunker-hill-series.ts` — never hand-edited.
// Engines are pure functions over it: the Scenario Engine applies
// perturbations (schema/scenario.ts) and the SPC engine (B6) computes control
// limits from the visible history. Nothing here stores a limit (D8).
//
// Time is plant-local wall-clock with no zone: 'YYYY-MM-DDTHH:mm'. Never build
// a JS Date from one of these strings without treating the fields as UTC
// (`Date.UTC(y, m - 1, d, hh, mm)`) — a bare `new Date('2026-06-08T11:30')` is
// interpreted in the host zone and the sweep captures diverge by machine.

/** Plant-local wall-clock timestamp, minute precision, no zone. */
export type SeriesTime = string;

/**
 * How often the quantity is observed. Decides the spacing of `points`:
 *   hourly  60 min   on-line instruments (WIT-021, JT-023, pH, cyclone pressure)
 *   shift   12 h     shift composites and rush assays (SA01 P80, product grades, shift tonnes)
 *   daily   24 h     metal-accounting figures (recovery, mill utilisation, t/d)
 */
export type SeriesCadence = 'hourly' | 'shift' | 'daily';

export const CADENCE_MINUTES: Record<SeriesCadence, number> = {
  hourly: 60,
  shift: 12 * 60,
  daily: 24 * 60,
};

/**
 * Bunker Hill's operating calendar. Day shift 06:00–18:00, night shift
 * 18:00–06:00. A production day runs night-then-day and is labelled by the
 * date of its day shift, so "Sun 08" = 07 Jun 18:00 → 08 Jun 18:00 — that is
 * how the deck reports "day 870 t / night 850 t" for today at 11:30.
 */
export const PLANT_CALENDAR = {
  dayShiftStart: '06:00',
  nightShiftStart: '18:00',
  /** The deck's Monday. Plant weekday = ((days since weekEpoch) mod 7) + 1; the civil calendar has 08 Jun 2026 on a Monday, the deck on a Sunday — the seed follows the deck. */
  weekEpoch: '2026-06-02',
} as const;

export type ReadingSeries = {
  /** Stable id (`rs-*`). Perturbations target a series, not a reading. */
  id: string;
  /**
   * The Reading rows this series feeds. Usually one; more than one where B4
   * left the same quantity under two ids (e.g. `rd-kpi-zn-grade` on the plant
   * and `rd-spec-zn-grade` on the concentrate stream). Every listed reading's
   * `value` is the series' latest visible point at the cursor — the seed's
   * stored `value` is that point at the default cursor, never a second truth.
   */
  readingIds: readonly string[];
  cadence: SeriesCadence;
  /**
   * Timestamp of `points[0]`. Every point is stamped at the START of its
   * period: an hourly point at 11:00 is the 11:00 observation; a shift point
   * at 06:00 is that day shift's composite; a daily point at 18:00 (previous
   * calendar day) is that production day's figure. A point is visible at
   * cursor `t` iff `stamp ≤ t`, so the latest visible point is the current
   * period's to-date figure — which is what the deck shows at 11:30.
   */
  start: SeriesTime;
  /**
   * One entry per period from `start`, `null` where no observation exists
   * (a night-only series has `null` at every day-shift stamp; a missed lab
   * sample is `null`). Numbers carry the reading's own `unit` and precision.
   */
  points: readonly (number | null)[];
  /**
   * Every B5 series is synthesised — say how: the shape (mean · σ · drift),
   * what pins it to documented figures ("last 7 daily points = deck 7-day
   * table"), and the `[syn]` prefix. The SPC engine never reads this.
   */
  provenance: string;
};

/** The seeded window: fifteen production days ending with today's day shift. */
export const SERIES_WINDOW = {
  start: '2026-05-24T18:00',
  /** Exclusive — the last hourly stamp is 17:00 on 08 Jun. */
  end: '2026-06-08T18:00',
} as const;

/** The session cursor may not leave the seeded window, and keeps a week of
 * history behind it so every 7-day projection has its points. */
export const CURSOR_BOUNDS = {
  min: '2026-06-01T06:00',
  max: '2026-06-08T17:59',
} as const;

/** Where the mock's "now" sits on load — `BUNKER_HILL_SHIFT` as a time. */
export const DEFAULT_CURSOR: SeriesTime = '2026-06-08T11:30';

/** A series after the cursor and perturbations are applied — what charts read. */
export type SeriesView = {
  seriesId: string;
  cadence: SeriesCadence;
  /** Visible points only (stamp ≤ cursor), perturbations applied per stamp. */
  points: readonly { at: SeriesTime; value: number | null }[];
  /** Latest visible non-null point with the perturbation evaluated AT THE
   * CURSOR (not at the point's stamp) — the live "now" a Reading.value shows.
   * `at` is the point's stamp, or a `set` perturbation's `from` when that is
   * later: a set is a new observation (an assay landing), and time-aware
   * readers must see it as such. */
  latest?: { at: SeriesTime; value: number };
};
